import { error as logError } from '@/utils/debugLogger';
import { $handleError } from '@/utils';
import feedbackManagementService from '@Feedback/service/feedbackManagement.service';
import companyManagementService from '@Company/service/company-management.service';
import { FEEDBACK_COMPANY_LOOKUP_PAGE_SIZE } from '@Feedback/constants/feedbackConstants';

/**
 * Management-side Feedback state (AVA-349): the platform-wide Interval and the
 * Feedback Round trigger.
 *
 * Kept separate from the FEEDBACK module on purpose. That one is every user's
 * prompt; this one is a Management-only admin surface, and merging them would
 * put admin state in every authenticated session's store.
 */
export const FEEDBACK_MANAGEMENT = {
  namespaced: true,

  state: {
    /** { intervalDays, configuredIntervalDays, requestedAt } — null until loaded. */
    settings: null,
    loading: false,
    saving: false,
    triggeringRound: false,
    /** Result of the round this session triggered: { requestedAt, snoozesCleared }. */
    lastRound: null,

    // ---- submissions list -------------------------------------------------
    submissions: [],
    submissionsTotal: 0,
    submissionsLoading: false,
    /** Companies for the filter dropdown: [{ id, name }]. */
    companies: [],
  },

  getters: {
    /**
     * True while no interval has ever been stored — the endpoint then reports the
     * platform default, and the field should say so rather than implying someone
     * chose 90.
     */
    IS_USING_DEFAULT_INTERVAL(state) {
      return !!state.settings && state.settings.configuredIntervalDays === null;
    },
  },

  mutations: {
    SET_SUBMISSIONS(state, { items, total }) {
      state.submissions = items;
      state.submissionsTotal = total;
    },
    SET_SUBMISSIONS_LOADING(state, value) {
      state.submissionsLoading = value;
    },
    SET_COMPANIES(state, value) {
      state.companies = value;
    },
    SET_SETTINGS(state, value) {
      state.settings = value;
    },
    SET_LOADING(state, value) {
      state.loading = value;
    },
    SET_SAVING(state, value) {
      state.saving = value;
    },
    SET_TRIGGERING_ROUND(state, value) {
      state.triggeringRound = value;
    },
    SET_LAST_ROUND(state, value) {
      state.lastRound = value;
    },
  },

  actions: {
    async LOAD_SETTINGS({ commit }) {
      commit('SET_LOADING', true);
      try {
        const res = await feedbackManagementService.getSettings();
        commit('SET_SETTINGS', res.data);
      } catch (err) {
        logError('FEEDBACK_MANAGEMENT: load settings failed', err);
        $handleError(err);
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * @returns {Promise<boolean>} whether the write landed, so the view can decide
     *          what to say without duplicating the error handling.
     */
    async SAVE_INTERVAL({ commit }, intervalDays) {
      commit('SET_SAVING', true);
      try {
        const res = await feedbackManagementService.updateSettings(intervalDays);
        commit('SET_SETTINGS', res.data);
        return true;
      } catch (err) {
        logError('FEEDBACK_MANAGEMENT: save interval failed', err);
        $handleError(err);
        return false;
      } finally {
        commit('SET_SAVING', false);
      }
    },

    /**
     * Re-prompt every user on their next session. Reloads settings afterwards so
     * the displayed "last round" reflects what the server actually stored rather
     * than what we hoped it stored.
     */
    async TRIGGER_ROUND({ commit, dispatch }) {
      commit('SET_TRIGGERING_ROUND', true);
      try {
        const res = await feedbackManagementService.triggerRound();
        commit('SET_LAST_ROUND', res.data);
        await dispatch('LOAD_SETTINGS');
        return true;
      } catch (err) {
        logError('FEEDBACK_MANAGEMENT: trigger round failed', err);
        $handleError(err);
        return false;
      } finally {
        commit('SET_TRIGGERING_ROUND', false);
      }
    },

    /**
     * One page of submissions. Every filter is a server parameter — the list is
     * server-paged, so filtering client-side would search only the visible page
     * while the total kept claiming the whole set.
     * @param {{ page, itemsPerPage, companyId, ratings, createdFrom, createdTo,
     *           sortBy, sortDesc }} query
     */
    async LOAD_SUBMISSIONS({ commit }, query) {
      commit('SET_SUBMISSIONS_LOADING', true);
      try {
        const res = await feedbackManagementService.list(query);
        commit('SET_SUBMISSIONS', {
          items: res.data.items || [],
          total: res.data.total || 0,
        });
      } catch (err) {
        logError('FEEDBACK_MANAGEMENT: load submissions failed', err);
        $handleError(err);
        commit('SET_SUBMISSIONS', { items: [], total: 0 });
      } finally {
        commit('SET_SUBMISSIONS_LOADING', false);
      }
    },

    /**
     * Companies for the filter dropdown. Deliberately a separate call rather than
     * derived from the loaded rows: under server paging those would only ever be
     * the companies on the current page.
     */
    async LOAD_COMPANIES({ commit, state }) {
      if (state.companies.length) return;
      try {
        const res = await companyManagementService.get({
          page: 1,
          itemsPerPage: FEEDBACK_COMPANY_LOOKUP_PAGE_SIZE,
        });
        const items = (res.data && res.data.items) || [];
        commit(
          'SET_COMPANIES',
          items
            .map((c) => ({ id: c.id, name: c.name }))
            .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
        );
      } catch (err) {
        logError('FEEDBACK_MANAGEMENT: load companies failed', err);
      }
    },
  },
};

export default FEEDBACK_MANAGEMENT;
